<template>
  <button
    class="base-button"
    :class="`base-button--${variant}`"
    :disabled="disabled || loading"
  >
    <!-- Afficher l'icône de chargement si 'loading' est vrai, sinon afficher le contenu du slot -->
    <FeatherIcon
      v-if="loading"
      type="loader"
      size="18"
      animation="spin"
    ></FeatherIcon>
    <span v-else>
      <slot></slot>
      <!-- Permet d'insérer du texte ou d'autres icônes dans le bouton -->
    </span>
  </button>
</template>

<script>
import FeatherIcon from "vue-feather";

export default {
  name: "BaseButton",
  components: {
    FeatherIcon, // Déclarer FeatherIcon pour l'utiliser dans le template
  },
  props: {
    variant: {
      type: String,
      default: "primary", // 'primary', 'secondary', 'accent', 'danger', 'text'
      validator: (value) =>
        ["primary", "secondary", "accent", "danger", "text"].includes(value),
    },
    // Nouvelle prop pour gérer l'état de chargement
    loading: {
      type: Boolean,
      default: false,
    },
    // Prop standard disabled pour les boutons
    disabled: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/css/_variables.scss";

.base-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: $space-sm $space-md;
  border: none;
  border-radius: $border-radius-sm;
  cursor: pointer;
  font-weight: bold;
  font-size: $font-size-base;
  transition: background-color $transition-duration-short
      $transition-ease-in-out,
    transform $transition-duration-short $transition-ease-in-out,
    box-shadow $transition-duration-short $transition-ease-in-out,
    opacity $transition-duration-short ease-in-out; // Ajouter transition pour opacity
  outline: none;

  // Style pour le contenu du slot (texte/icônes)
  span {
    display: flex;
    align-items: center; // Assure l'alignement vertical si le slot contient du texte + icône
    .feather-icon {
      margin-right: $space-xs; // Espace entre l'icône et le texte
    }
  }

  // --- Variantes ---
  &--primary {
    background-color: $color-primary-500;
    color: $color-surface;
    &:not(:disabled):hover {
      // Appliquer au survol sauf si disabled
      background-color: darken($color-primary-500, 10%);
      transform: translateY(-1px);
      box-shadow: $shadow-sm;
    }
    &:not(:disabled):active {
      // Appliquer au clic sauf si disabled
      transform: translateY(0);
      box-shadow: none;
    }
  }

  &--accent {
    background-color: $color-accent-500;
    color: $color-surface;
    &:not(:disabled):hover {
      background-color: darken($color-accent-500, 10%);
      transform: translateY(-1px);
      box-shadow: $shadow-sm;
    }
    &:not(:disabled):active {
      transform: translateY(0);
      box-shadow: none;
    }
  }

  &--secondary {
    background-color: $color-background;
    color: $color-text-900;
    border: 1px solid $color-primary-100;
    &:not(:disabled):hover {
      background-color: darken($color-background, 5%);
      border-color: $color-primary-500;
      transform: translateY(-1px);
      box-shadow: $shadow-sm;
    }
    &:not(:disabled):active {
      transform: translateY(0);
      box-shadow: none;
    }
  }

  &--danger {
    background-color: $color-error;
    color: $color-surface;
    &:not(:disabled):hover {
      background-color: darken($color-error, 10%);
      transform: translateY(-1px);
      box-shadow: $shadow-sm;
    }
    &:not(:disabled):active {
      transform: translateY(0);
      box-shadow: none;
    }
  }

  &--text {
    background: none;
    border: none;
    color: $color-primary-500;
    padding: $space-xs $space-sm;
    &:not(:disabled):hover {
      text-decoration: underline;
    }
    &:not(:disabled):active {
      transform: scale(0.98);
    }
  }

  // Style pour le bouton désactivé
  &:disabled {
    opacity: 0.6; // Rendre le bouton semi-transparent
    cursor: not-allowed; // Afficher le curseur "non autorisé"
    transform: none !important; // Annuler les transformations au survol/actif
    box-shadow: none !important; // Annuler les ombres
  }
}
</style>
